import { uid } from 'uid';
import { LitElement, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { FocusMixin } from '@fil-global/shared';
import '@fil-global/fil-tooltip';
import '@fil-global/fil-label';

import { STATUS } from './util.js';
import formFieldStyles from './text-field.styles.js';

/**
 * @property {'primary'|'secondary'} [appearance=primary] Appearance of the component
 * @property {string} [assistiveText] Assistive text to be used in the component
 * @property {string}[value] The element value
 * @property {string}[name] Label name
 * @property {string}[errorText] Error description
 * @property {boolean}[disabled=false] HTML disabled attribute of the component
 * @property {string}[status] validate result
 * @property {string}[size] element size
 * @property {string} [label] Label of the component
 * @property {"1-col"|"2-cols"} [layout=1-col] Layout of the component
 * @property {"currency"|"dob"|"number"|"nationalInsuranceNumber"|"percentage"} [mask]
 * @property {string}[tooltip]
 * @property {boolean}[required=false] Whether the element is required
 *
 * */
export class TextField extends FocusMixin(LitElement) {
  static get properties() {
    return {
      appearance: { type: String }, // 'primary' | 'secondary'
      assistiveText: { type: String },
      disabled: { type: Boolean },
      errorText: { type: String },
      label: { type: String },
      layout: { type: String }, // "1-col" | "2-cols"
      mask: { type: String }, // "dob" | "currency" | "nationalInsuranceNumber"
      name: { type: String },
      required: { type: Boolean },
      size: { type: String },
      status: { type: String }, // 'disabled' | 'invalid' | 'valid'
      tooltip: { type: String },
      tooltipExtraProps: { type: Object },
      value: { type: String },
    };
  }

  static get styles() {
    return [formFieldStyles];
  }

  constructor() {
    super();
    this.appearance = 'primary';
    this.disabled = false;
    this.layout = '1-col';
    this.tooltipExtraProps = { position: 'bottom', alignment: 'right' };

    this.elementId = uid();
  }

  connectedCallback() {
    super.connectedCallback();
    this.invalid = this.status === STATUS.INVALID;
  }

  updated() {
    const el = this.shadowRoot.querySelector('select,textarea,input');
    if (el) {
      if (this.status === STATUS.INVALID) {
        el.setAttribute('aria-invalid', 'true');
      } else {
        el.removeAttribute('aria-invalid');
      }
    }
  }

  maskTemplate() {
    return this.mask === 'percentage' ? html`<span class="fil-text-field__mask">%</span>` : nothing;
  }

  renderTemplates() {
    return html`<div class="fil-text-field__description">
        ${this.label || this.assistiveText || this.status === STATUS.INVALID
          ? html`<fil-label
        assistiveText="${this.assistiveText}"
        ?disabled=${this.disabled || this.status === STATUS.DISABLED}
        ?required=${this.required}
        errorText="${ifDefined(this.status === STATUS.INVALID ? this.errorText || ' ' : undefined)}"
        for=${ifDefined(this.name)}"
        tooltip="${ifDefined(this.tooltip)}"
        extraProps=${
          ifDefined(this.tooltipExtraProps)
            ? JSON.stringify({ ...this.tooltipExtraProps })
            : undefined
        }
        size="${this.size}"
        >${this.label}</fil-label>`
          : nothing}
      </div>
      <div class="fil-text-field__item">${this.renderElement()} ${this.maskTemplate()}</div>`;
  }

  render() {
    const classes = {
      [`fil-text-field--${this.appearance}`]: this.appearance !== 'primary',
      'fil-text-field--valid': this.status === STATUS.VALID,
      'fil-text-field--invalid': this.status === STATUS.INVALID,
      'fil-text-field--2-cols': this.layout === '2-cols',
      'fil-text-field--disabled': this.disabled || this.status === STATUS.DISABLED,
      [`fil-text-field--${this.size}`]: this.size,
      'fil-text-field--with-mask': !!this.mask,
    };

    return html`<div class="fil-text-field${classMap(classes)}">${this.renderTemplates()}</div>`;
  }
}

export default TextField;
